import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { ProductStatus } from './ProductStatus';
import { BrandDiscounts } from './BrandDiscounts';
import { useAdmin } from '../AdminContext';
import { skinTypes } from '../../data/concerns';
import { asSkinType, errorCount, productChecks } from './catalogRules';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';

/** De parameters in het webadres die deze lijst filteren. */
const FILTER_KEYS = ['merk', 'thema', 'filter'] as const;

/** Alle producten, te filteren op merk, huidthema en problemen. De filters staan in het webadres. */
export function Products() {
  const {
    catalog
  } = useAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // De zoekterm staat ook in het adres: na een product openen en Terug staat hij er nog.
  const query = params.get('zoek') ?? '';
  const brand = params.get('merk') ?? '';
  const tag = asSkinType(params.get('thema') ?? '');
  const onlyIncomplete = params.get('filter') === 'onvolledig';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const setQuery = (value: string) => setParam('zoek', value);

  /** De filters die nu iets wegfilteren, in gewone woorden. */
  const activeFilters = [brand, tag ?? '', onlyIncomplete ? 'alleen met problemen' : ''].filter(Boolean);

  /** Haalt merk, thema en "met problemen" uit het adres; andere parameters blijven staan. */
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const key of FILTER_KEYS) next.delete(key);
    setParams(next, {
      replace: true
    });
  };

  // De controles hangen alleen van de producten af, niet van de zoekterm: één keer per wijziging.
  const checked = useMemo(() => catalog.products.map(p => ({
    p,
    checks: productChecks(p),
    text: `${p.name} ${p.brand} ${p.category} ${p.keyIngredients.join(' ')}`.toLowerCase()
  })), [catalog.products]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return checked.filter(({
      p
    }) => !brand || p.brand === brand).filter(({
      p
    }) => !tag || p.skinTypes.includes(tag)).filter(r => !q || r.text.includes(q)).filter(r => !onlyIncomplete || errorCount(r.checks) > 0);
  }, [checked, brand, tag, query, onlyIncomplete]);
  return <div data-id="element-760" data-mp="2znugykgptqx5">
      <PageHeader title="Producten" description={`${plural(catalog.products.length, 'product', 'producten')} in de webshop. Klik op een product om het aan te passen.`} actions={<Link to="/be/producten/nieuw" className={btnPrimary} data-id="element-762" data-mp="f1j3pdguqaau">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-763" data-mp="p3528o6m6mpg" />
            Nieuw product
          </Link>} data-id="element-761" data-mp="2znkq6unujfqe" />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center" data-id="element-764" data-mp="fdtub4vgro35">
        <label className="relative col-span-2 min-w-[14rem] flex-1" data-id="element-765" data-mp="fbh4k5jgs9fj">
          <span className="sr-only" data-id="element-766" data-mp="bbqek41rhxc2">Zoeken</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-767" data-mp="6cvlm9a2yse8" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek op naam, merk of ingrediënt…" className={`${inputCls} pl-9`} data-id="element-768" data-mp="1cr54j969xo0" />
        </label>
        <select value={brand} onChange={e => setParam('merk', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Merk" data-id="element-769" data-mp="3ojtins9qjws8">
          <option value="" data-id="element-770" data-mp="1qqwavzxkcdnb">Alle merken</option>
          {catalog.brands.map(b => <option key={b.name} data-id="element-771" data-mp="1ms6oxiyglns2">{b.name}</option>)}
        </select>
        <select value={tag ?? ''} onChange={e => setParam('thema', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Huidthema" data-id="element-772" data-mp="1tvwr7tytx2h3">
          <option value="" data-id="element-773" data-mp="1yrbzm6e2i664">Alle huidthema’s</option>
          {skinTypes.map(s => <option key={s} data-id="element-774" data-mp="1kvzwbaluj7dl">{s}</option>)}
        </select>
        <button type="button" aria-pressed={onlyIncomplete} onClick={() => setParam('filter', onlyIncomplete ? '' : 'onvolledig')} className={`col-span-2 rounded-full border px-4 py-2.5 text-[14px] transition-colors duration-150 ease-soft sm:py-2 sm:text-[13px] ${onlyIncomplete ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-775" data-mp="1ou7bi10nw0mo">
          Alleen met problemen
        </button>
      </div>

      <p className="mb-2 text-[13px] text-muted md:hidden" role="status" data-id="element-776" data-mp="s5rni9d79zbo">
        {plural(rows.length, 'resultaat', 'resultaten')}
      </p>
      <div className="overflow-hidden rounded-lg border border-line bg-white" data-id="element-777" data-mp="dij7mv8y55td">
        {/* Mobiel: één product per rij, groot genoeg voor je duim */}
        <ul className="divide-y divide-line md:hidden" data-id="element-778" data-mp="8krejxwkfdnl">
          {rows.map(({
          p,
          checks
        }) => <li key={p.id} data-id="element-779" data-mp="mk0inz7hyhb7">
              <Link to={`/be/producten/${p.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-shell" data-id="element-780" data-mp="mh4mogf7m2pe">
                {p.image ? <ProductImage src={p.image} width={56} loading="lazy" className="h-14 w-14 shrink-0 rounded p-1" data-id="element-781" data-mp="wgn3inztp8d8" /> : <span className="h-14 w-14 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-782" data-mp="ri224l8u4in7" />}
                <span className="min-w-0 flex-1" data-id="element-783" data-mp="169az5ff2h6ad">
                  <span className="block truncate text-[15px] text-ink" data-id="element-784" data-mp="117vcjix8qrpv">{p.name || 'Naamloos product'}</span>
                  <span className="block truncate text-[13px] text-muted" data-id="element-785" data-mp="1f8y2w7v9qo6l">
                    {p.brand} · {p.step ?? p.category}
                  </span>
                  <span className="mt-0.5 block" data-id="element-786" data-mp="1a6z0b07crvap">
                    <ProductStatus product={p} checks={checks} data-id="element-787" data-mp="7wowjnntb8v9" />
                  </span>
                </span>
                <span className="shrink-0 text-[15px] tabular-nums text-ink" data-id="element-788" data-mp="cv05lw6a9fcf">{euro(p.price, {
                cents: true
              })}</span>
              </Link>
            </li>)}
        </ul>
        <div className="hidden overflow-x-auto md:block" data-id="element-789" data-mp="ln27lyw033ot">
          <table className="w-full min-w-[760px] text-left text-[14px]" data-id="element-790" data-mp="lomwb7vmvser">
            <thead className="border-b border-line bg-shell text-[12px] text-muted" data-id="element-791" data-mp="qln71w3fhjc7">
              <tr data-id="element-792" data-mp="vjevuh8s0pc6">
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-793" data-mp="vn2s3o5lvpul">Product</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-794" data-mp="10kkqwlpwsoov">Stap</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-795" data-mp="14lxyp1ckb1yf">Huidthema’s</th>
                <th scope="col" className="px-4 py-3 text-right font-normal" data-id="element-796" data-mp="32bxtv58bgmia">Prijs</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-797" data-mp="36d167hr5o0er">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-798" data-mp="2yu1rhpen2x1q">
              {rows.map(({
              p,
              checks
            }) =>
            // De hele rij is klikbaar voor de muis; de link in de eerste cel is er voor toetsenbord en schermlezer.
            <tr key={p.id} onClick={() => navigate(`/be/producten/${p.id}`)} className="cursor-pointer transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-799" data-mp="2p4qkc8bbc2gh">
                  <td className="px-4 py-3" data-id="element-800" data-mp="3hs4b1oof41s5">
                    <Link to={`/be/producten/${p.id}`} onClick={e => e.stopPropagation()} className="flex items-center gap-3" data-id="element-801" data-mp="3hruibtbzngir">
                      {p.image ? <ProductImage src={p.image} width={44} loading="lazy" className="h-11 w-11 shrink-0 rounded p-0.5" data-id="element-802" data-mp="38r6cvn0nuohh" /> : <span className="h-11 w-11 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-803" data-mp="33upgy6n7z5tu" />}
                      <span className="min-w-0" data-id="element-804" data-mp="1sdfzfpnninv6">
                        <span className="block truncate text-ink" data-id="element-805" data-mp="1scn0vbyuj4o4">{p.name || 'Naamloos product'}</span>
                        <span className="block text-[12px] text-muted" data-id="element-806" data-mp="3sew6yoinun1s">
                          {p.brand} · {p.category}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted" data-id="element-807" data-mp="3ijulq3ria54h">{p.step ?? '—'}</td>
                  <td className="px-4 py-3" data-id="element-808" data-mp="3nh4he15hnd0r">
                    <span className="flex flex-wrap gap-1" data-id="element-809" data-mp="f11bo0xxryf2">
                      {p.skinTypes.map(s => <span key={s} className="rounded-full bg-sand px-2 py-0.5 text-[12px] text-ink" data-id="element-810" data-mp="k0dv0bplmncw">
                          {s}
                        </span>)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-ink" data-id="element-811" data-mp="a8hzn8ewdbcd">{euro(p.price, {
                  cents: true
                })}</td>
                  <td className="px-4 py-3" data-id="element-812" data-mp="xxu3dsrggr7a">
                    <ProductStatus product={p} checks={checks} data-id="element-813" data-mp="12rf4htoci1ap" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <EmptyState icon={SearchIcon} className="m-4 border-0 bg-transparent" title="Geen producten gevonden" text={emptyText(query.trim(), activeFilters)} action={<span className="flex flex-wrap justify-center gap-2" data-id="element-815" data-mp="2s9lsfnqd3mt0">
                {/* Eerst de zoekterm: dat is meestal de typfout. Bewust gekozen filters blijven staan. */}
                {query.trim() && <button type="button" onClick={() => setQuery('')} className={activeFilters.length ? btnPrimary : btnSecondary} data-id="element-816" data-mp="1zl6d103bj2h5">
                    Zoekterm wissen
                  </button>}
                {activeFilters.length > 0 && <button type="button" onClick={clearFilters} className={btnSecondary} data-id="element-817" data-mp="3owgs47i3r1el">
                    {activeFilters.length === 1 ? `Toon zonder “${activeFilters[0]}”` : 'Filters loslaten'}
                  </button>}
              </span>} data-id="element-814" data-mp="2igwjaziaal0u" />}
      </div>

      <div className="mt-8 max-w-2xl" data-id="element-818" data-mp="1032ypjcnwtaz">
        <BrandDiscounts data-id="element-819" data-mp="vbtbepel6229" />
      </div>
    </div>;
}

/** Zegt precies waarbinnen er niets gevonden is, zodat Sarah ziet wat ze moet loslaten. */
function emptyText(query: string, filters: string[]) {
  const within = filters.length ? ` binnen ${filters.join(' · ')}` : '';
  if (query) return `Niets gevonden voor “${query}”${within}.`;
  return `Geen producten${within}.`;
}